import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import "./index.css";
import "whatwg-fetch";

import React from "react";
import ReactDOM from "react-dom";
import { HashRouter, Route, Switch } from "react-router-dom";
import JssProvider from "react-jss/lib/JssProvider";

import { createStore, combineReducers, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import { createLogger } from "redux-logger";
import { Provider as ReduxProvider } from "react-redux";
import { createReducer } from "redux-orm";
import { offline } from "@redux-offline/redux-offline";
import offlineConfig from "@redux-offline/redux-offline/lib/defaults";
import localForage from "localforage";

import { MuiThemeProvider, createMuiTheme, createGenerateClassName } from "@material-ui/core/styles";

import HomePage from "./components/Form";

import registerServiceWorker from "./registerServiceWorker";

import navReducer from "./components/common/reducers";
import orm, { reloadAll, syncReducer } from "./components/common/orm";

const theme = createMuiTheme({
    props: {
        MuiButton: {
            disableRipple: true
        }
    },
    typography: {
        fontFamily: "Roboto, 'Helvetica Neue', Arial, sans-serif"
    },
    focusVisible: {
        boxShadow: "0 0 0 0.2rem rgba(0,102,0,.50) !important",
        opacity: ".9"
    },
    overrides: {
        MuiInputLabel: {
            root: {
                fontSize: 14,
                color: "#212529 !important"
            },
            shrink: {
                transform: "translate(0, 0px) scale(1)"
            }
        },
        MuiListItem: {
            root: {
                fontSize: 16,
                fontWeight: 400,
                color: "#212529"
            }
        },
        MuiFormLabel: {
            root: {
                fontSize: 14,
                fontWeight: 400,
                color: "#212529"
            }
        },
        MuiFormHelperText: {
            root: {
                fontSize: 14,
            },
            error: {
                color: "#be0000 !important"
            }
        },
        MuiTypography: {
            h2: {
                // Title of pages
                color: "#495057",
                fontSize: "1.55rem",
                lineHeight: 1.17
            },
            h3: {
                // Section titles
                fontWeight: 500,
                fontSize: "1.15rem",
                color: "#495057",
                lineHeight: 1.2
            },
            body1: {
                // Regular text
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.5,
                color: "#212529"
            },
        },
        MuiButton: {
            root: {
                textTransform: "none",
                color: "#707070",
                "&:hover": {
                    backgroundColor: "#E0E0E0",
                    textDecoration: "none"
                },
                border: "1px solid",
            },
            contained: {
                border: "none",
            },
            focusVisible: {
                boxShadow: "0 0 0 0.2rem rgba(0,102,0,.50) !important",
                opacity: ".9"
            },
        }
    },
    palette: {
        primary: {
            light: "#006600",
            main: "#006600",
            dark: "#003600",
            contrastText: "#fff"
        }
    }
});

const generateClassName = createGenerateClassName({
    productionPrefix: "mmcd"
});

const logger = createLogger();
const reducer = combineReducers({
    orm: createReducer(orm),
    sync: syncReducer,
    nav: navReducer
});

const initialState = {
    nav: {
        sw: { checking: true },
    }
};

const middleware = process.env.NODE_ENV === "production" ? applyMiddleware(thunk) : applyMiddleware(thunk, logger);

const store = createStore(
    reducer,
    initialState,
    compose(
        middleware,
        offline({
            ...offlineConfig,
            retry: (action, retries) => (retries > 3 ? null : retries * 1000),
            persistOptions: {
                storage: localForage,
                serialize: false
            },
            persistAutoRehydrate: () =>
                offlineConfig.persistAutoRehydrate({
                    stateReconciler: (state, inboundState) => {
                        // Don't carry over pending from previous session
                        // Loading icon becomes stuck
                        if (inboundState.auth && inboundState.auth.pending) {
                            inboundState.auth.pending = null;
                        }
                        // Don't wipe out sw notification if it happened during init
                        if (state.nav && state.nav.sw) {
                            inboundState.nav = {
                                ...inboundState.nav,
                                sw: state.nav.sw
                            };
                        }
                        inboundState.offline = {
                            ...state.offline,
                            ...inboundState.offline,
                            online: state.offline.online,
                            netInfo: state.offline.netInfo,
                            busy: state.offline.busy
                        };

                        inboundState.orm = {
                            ...state.orm,
                            ...inboundState.orm
                        };
                        return inboundState;
                    }
                }),
            persistCallback: () => {
                //store.dispatch(storeReady());
                store.dispatch(reloadAll(true));
            }
        })
    )
);

ReactDOM.render(
    <ReduxProvider store={store}>
        <JssProvider generateClassName={generateClassName}>
            <MuiThemeProvider theme={theme}>
                <HashRouter >
                    <Switch>
                        <Route exact path="/" component={HomePage} />
                    </Switch>
                </HashRouter>
            </MuiThemeProvider>
        </JssProvider>
    </ReduxProvider>,
    document.getElementById("root")
);

registerServiceWorker(store.dispatch);
